/**
 * DatePicker — Airbnb-inspired calendar picker.
 *
 * Modes
 *   • 'single' – pick one date; calls onChange(isoString)
 *   • 'range'  – pick start + end; calls onRangeChange(startIso, endIso|null)
 *                Shows two months side-by-side.
 *
 * Props
 *   minDate   – ISO date string; dates before this are disabled / greyed out.
 *   The picker renders inline below a trigger input.
 */

import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ─── helpers ──────────────────────────────────────────────────────────────────

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay(); // 0 = Sunday
}

function addMonths(year: number, month: number, delta: number) {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

function compareDates(a: string, b: string) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function formatDisplay(iso: string): string {
  const [y, m, d] = iso.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// ─── MonthGrid ────────────────────────────────────────────────────────────────

interface MonthGridProps {
  year: number;
  month: number;
  today: string;
  minDate?: string;
  maxDate?: string;
  /** For single mode */
  selected?: string | null;
  /** For range mode */
  rangeStart?: string | null;
  rangeEnd?: string | null;
  hoverDate?: string | null;
  onDayClick: (iso: string) => void;
  onDayHover: (iso: string | null) => void;
}

const MonthGrid: React.FC<MonthGridProps> = ({
  year,
  month,
  today,
  minDate,
  maxDate,
  selected,
  rangeStart,
  rangeEnd,
  hoverDate,
  onDayClick,
  onDayHover,
}) => {
  const totalDays = daysInMonth(year, month);
  const startOffset = firstDayOfMonth(year, month);

  // Effective range end (use hover date when end not yet chosen)
  const effectiveEnd = rangeEnd ?? (rangeStart ? hoverDate : null);

  const cells: Array<{ day: number | null; iso: string | null }> = [];
  for (let i = 0; i < startOffset; i += 1) cells.push({ day: null, iso: null });
  for (let d = 1; d <= totalDays; d += 1)
    cells.push({ day: d, iso: isoDate(year, month, d) });

  return (
    <div className="select-none">
      {/* Weekday row */}
      <div className="mb-1 grid grid-cols-7 text-center">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="py-0.5 text-[10px] font-semibold text-gray-400"
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (!cell.iso || cell.day === null) {
            // eslint-disable-next-line react/no-array-index-key
            return <div key={`pad-${idx}`} className="h-9" />;
          }

          const { iso } = cell;
          const disabled =
            (!!minDate && compareDates(iso, minDate) < 0) ||
            (!!maxDate && compareDates(iso, maxDate) > 0);
          const isToday = iso === today;

          // Single-mode selection
          const isSingleSelected = selected ? iso === selected : false;

          // Range states
          const isRangeStart = rangeStart ? iso === rangeStart : false;
          const isRangeEnd = rangeEnd ? iso === rangeEnd : false;

          // Is inside the range (exclusive of endpoints for background strip)
          let isInRange = false;
          if (rangeStart && effectiveEnd) {
            const [lo, hi] =
              compareDates(rangeStart, effectiveEnd) <= 0
                ? [rangeStart, effectiveEnd]
                : [effectiveEnd, rangeStart];
            isInRange = compareDates(iso, lo) > 0 && compareDates(iso, hi) < 0;
          }

          // Endpoint (circle) — either rangeStart or rangeEnd
          const isEndpoint = isRangeStart || isRangeEnd;

          // Background strip colours
          let rangeBg = '';
          if (isEndpoint) {
            // Determine strip halves
            const lo =
              rangeStart &&
              effectiveEnd &&
              compareDates(rangeStart, effectiveEnd) <= 0
                ? rangeStart
                : (effectiveEnd ?? rangeStart);
            const hi =
              rangeStart &&
              effectiveEnd &&
              compareDates(rangeStart, effectiveEnd) > 0
                ? rangeStart
                : (effectiveEnd ?? rangeStart);

            if (rangeStart && effectiveEnd && lo !== hi) {
              if (iso === lo) rangeBg = 'range-strip-right'; // right half
              if (iso === hi) rangeBg = 'range-strip-left'; // left half
            }
          } else if (isInRange) {
            rangeBg = 'range-strip-full';
          }

          return (
            <div
              key={iso}
              className={[
                'relative flex h-9 items-center justify-center',
                rangeBg === 'range-strip-full' ? 'bg-pos-primary/10' : '',
              ].join(' ')}
              onMouseEnter={() => onDayHover(iso)}
              onMouseLeave={() => onDayHover(null)}
            >
              {/* Half-strip for endpoints */}
              {rangeBg === 'range-strip-right' && (
                <span className="absolute right-0 top-0 h-full w-1/2 bg-pos-primary/10" />
              )}
              {rangeBg === 'range-strip-left' && (
                <span className="absolute left-0 top-0 h-full w-1/2 bg-pos-primary/10" />
              )}

              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onDayClick(iso)}
                className={[
                  'relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                  disabled
                    ? 'cursor-not-allowed text-gray-300'
                    : isEndpoint || isSingleSelected
                      ? 'bg-pos-primary text-white shadow-sm'
                      : isToday
                        ? 'border border-pos-primary text-pos-primary hover:bg-pos-primary/10'
                        : 'text-gray-700 hover:bg-pos-primary/10',
                ].join(' ')}
              >
                {cell.day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── DatePicker ───────────────────────────────────────────────────────────────

interface SingleProps {
  mode: 'single';
  value?: string | null;
  onChange: (iso: string) => void;
  startDate?: never;
  endDate?: never;
  onRangeChange?: never;
}

interface RangeProps {
  mode: 'range';
  startDate?: string | null;
  endDate?: string | null;
  onRangeChange: (start: string, end: string | null) => void;
  value?: never;
  onChange?: never;
}

type DatePickerProps = (SingleProps | RangeProps) & {
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  label?: string;
  id?: string;
  /** Extra class on the trigger element */
  className?: string;
  /**
   * Which edge of the trigger the calendar panel anchors to.
   * 'left' (default) — panel opens rightward, safe near left edge / sidebar.
   * 'right' — panel opens leftward, safe near right edge.
   */
  popoverAlign?: 'left' | 'right';
};

export const DatePicker: React.FC<DatePickerProps> = ({
  mode,
  minDate,
  maxDate,
  placeholder = 'Select date',
  label,
  id,
  className = '',
  popoverAlign = 'left',
  // single-mode props (undefined in range mode)
  value,
  onChange,
  // range-mode props (undefined in single mode)
  startDate,
  endDate,
  onRangeChange,
}) => {
  const today = new Date().toISOString().slice(0, 10);

  // ── popover state ──
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  // ── calendar navigation ──
  const now = new Date();
  const [leftYear, setLeftYear] = useState(now.getFullYear());
  const [leftMonth, setLeftMonth] = useState(now.getMonth());

  // Right month = one month ahead of left (only used in range mode)
  const { year: rightYear, month: rightMonth } = addMonths(
    leftYear,
    leftMonth,
    1,
  );

  // ── range picking state ──
  // When first date is chosen but second isn't yet, track hover for preview
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  // In range mode, track pending start (first click)
  const [pendingStart, setPendingStart] = useState<string | null>(null);

  // Derived values
  const rangeStart =
    mode === 'range' ? (pendingStart ?? startDate ?? null) : null;
  const rangeEnd = mode === 'range' && !pendingStart ? (endDate ?? null) : null;

  // Close popover when clicking outside (accounts for the portal-rendered popover)
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = containerRef.current?.contains(target) ?? false;
      const inPopover = popoverRef.current?.contains(target) ?? false;
      if (!inTrigger && !inPopover) {
        setOpen(false);
        setPendingStart(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── handlers ──
  const handleDayClick = (iso: string) => {
    if (mode === 'single') {
      onChange?.(iso);
      setOpen(false);
      return;
    }

    // Range mode: first click sets start, second sets end
    if (!pendingStart) {
      // No pending start — begin a new selection
      setPendingStart(iso);
      onRangeChange?.(iso, null);
    } else {
      // Second click — finalise range
      const [start, end] =
        compareDates(pendingStart, iso) <= 0
          ? [pendingStart, iso]
          : [iso, pendingStart];
      setPendingStart(null);
      onRangeChange?.(start, end);
      setOpen(false);
    }
  };

  const goPrevMonth = () => {
    const prev = addMonths(leftYear, leftMonth, -1);
    setLeftYear(prev.year);
    setLeftMonth(prev.month);
  };
  const goNextMonth = () => {
    const next = addMonths(leftYear, leftMonth, 1);
    setLeftYear(next.year);
    setLeftMonth(next.month);
  };

  // ── trigger display text ──
  let triggerText = '';
  if (mode === 'single') {
    triggerText = value ? formatDisplay(value) : '';
  } else {
    const s = pendingStart ?? startDate;
    const e = pendingStart ? null : endDate;
    if (s && e) {
      triggerText = `${formatDisplay(s)} → ${formatDisplay(e)}`;
    } else if (s) {
      triggerText = `${formatDisplay(s)} → …`;
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label
          htmlFor={id}
          className="mb-1 block text-xs font-medium text-gray-700"
        >
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        onClick={() => {
          setTriggerRect(triggerRef.current?.getBoundingClientRect() ?? null);
          setOpen((v) => !v);
        }}
        className={[
          'flex w-full items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm text-left',
          'focus:outline-none focus:ring-2 focus:ring-pos-primary/30',
          open
            ? 'border-pos-primary ring-2 ring-pos-primary/30'
            : 'hover:border-gray-400',
          className,
        ].join(' ')}
      >
        <CalendarDays size={14} className="shrink-0 text-gray-400" />
        <span className={triggerText ? 'text-gray-900' : 'text-gray-400'}>
          {triggerText || placeholder}
        </span>
      </button>

      {/* Popover — rendered via portal so it escapes overflow:hidden/auto containers */}
      {open &&
        triggerRect &&
        createPortal(
          <div
            ref={popoverRef}
            className={[
              'z-[9999] rounded-2xl border border-gray-200 bg-white p-4 shadow-xl',
              mode === 'range' ? 'flex gap-6' : '',
            ].join(' ')}
            style={{
              position: 'fixed',
              top: triggerRect.bottom + 6,
              left:
                popoverAlign === 'right'
                  ? Math.max(
                      0,
                      triggerRect.right - (mode === 'range' ? 560 : 280),
                    )
                  : triggerRect.left,
              minWidth: mode === 'range' ? 560 : 280,
            }}
          >
            {/* Left month */}
            <div className="flex-1">
              {/* Month nav header */}
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                  aria-label="Previous month"
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="text-xs font-semibold text-gray-800">
                  {MONTH_NAMES[leftMonth]} {leftYear}
                </span>
                {mode === 'single' ? (
                  <button
                    type="button"
                    onClick={goNextMonth}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                    aria-label="Next month"
                  >
                    <ChevronRight size={15} />
                  </button>
                ) : (
                  <div className="w-6" /> /* spacer */
                )}
              </div>

              <MonthGrid
                year={leftYear}
                month={leftMonth}
                today={today}
                minDate={minDate}
                maxDate={maxDate}
                selected={mode === 'single' ? (value ?? null) : undefined}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={setHoverDate}
              />
            </div>

            {/* Right month (range only) */}
            {mode === 'range' && (
              <div className="flex-1">
                <div className="mb-3 flex items-center justify-between">
                  <div className="w-6" /> {/* spacer */}
                  <span className="text-xs font-semibold text-gray-800">
                    {MONTH_NAMES[rightMonth]} {rightYear}
                  </span>
                  <button
                    type="button"
                    onClick={goNextMonth}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                    aria-label="Next month"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>

                <MonthGrid
                  year={rightYear}
                  month={rightMonth}
                  today={today}
                  minDate={minDate}
                  maxDate={maxDate}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  hoverDate={hoverDate}
                  onDayClick={handleDayClick}
                  onDayHover={setHoverDate}
                />
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
